import React from "react";

const PAGE_W = 842;
const PAGE_H = 595;
/** Preview scale — keep aspect identical to PDF page (A4 landscape). */

function assetBaseUrl() {
  const base = process.env.REACT_APP_API_BASE_URL || "";
  return base.replace(/\/api\/v1\/?$/i, "") || "";
}

function resolveUrl(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${assetBaseUrl()}${url.startsWith("/") ? url : `/${url}`}`;
}

function interpolate(str, data) {
  if (!str) return "";
  return String(str).replace(/\{\{([^}]+)\}\}/g, (_, key) => {
    const k = key.trim();
    if (k.startsWith("colors.")) {
      return data.colors?.[k.slice(7)] || "";
    }
    return data[k] ?? "";
  });
}

function CertificateTemplatePreview({ config, sampleData }) {
  const page = config?.page || { width: PAGE_W, height: PAGE_H };
  const scale = 0.55;
  const w = page.width * scale;
  const h = page.height * scale;

  const colors = config?.colors || {};
  const placeholders = config?.placeholders || {};
  const data = {
    header: placeholders.header || "CERTIFICATE",
    certificate_title: placeholders.certificate_title || "OF COMPLETION",
    subtitle: placeholders.subtitle || "This is to certify that",
    body_text: placeholders.body_text || "has successfully completed the course",
    signature_label: placeholders.signature_label || "Authorized Signature",
    user_name: sampleData?.user_name || "Sample Student",
    course_name: sampleData?.course_name || "Sample Course",
    score_text: sampleData?.percentage
      ? `with a score of ${sampleData.percentage}%`
      : "",
    date_text: `Date: ${new Date().toLocaleDateString()}`,
    instructor_block: sampleData?.taught_by
      ? `Instructor\n${sampleData.taught_by}`
      : "",
    logo_url: config?.logo_url,
    signature_url: config?.signature_url,
    colors,
  };

  const bg = config?.background || {};
  const bgStyle = {};
  if (bg.type === "image" && bg.image_url) {
    bgStyle.backgroundImage = `url(${resolveUrl(bg.image_url)})`;
    bgStyle.backgroundSize = "cover";
    bgStyle.backgroundPosition = "center";
  } else {
    bgStyle.backgroundColor = bg.color || "#ffffff";
  }

  const elements = config?.elements || [];

  return (
    <div className="border rounded-lg overflow-hidden bg-muted/30 p-4">
      <p className="text-xs text-muted-foreground mb-2">Live preview (scaled)</p>
      <div
        style={{ width: w, height: h, position: "relative", ...bgStyle }}
        className="shadow-md mx-auto"
      >
        {elements.map((el, i) => {
          if (el.type === "rect") {
            return (
              <div
                key={i}
                style={{
                  position: "absolute",
                  left: el.x * scale,
                  top: el.y * scale,
                  width: el.width * scale,
                  height: el.height * scale,
                  border: `${(el.lineWidth || 1) * scale}px solid ${interpolate(el.stroke, data)}`,
                  boxSizing: "border-box",
                  background: el.fill || "transparent",
                }}
              />
            );
          }
          if (el.type === "line") {
            return (
              <div
                key={i}
                style={{
                  position: "absolute",
                  left: el.x1 * scale,
                  top: el.y1 * scale,
                  width: (el.x2 - el.x1) * scale,
                  height: (el.lineWidth || 2) * scale,
                  background: interpolate(el.stroke, data),
                }}
              />
            );
          }
          if (el.type === "image") {
            const src = el.field ? data[el.field] : el.src;
            if (!src) return null;
            return (
              <img
                key={i}
                alt=""
                src={resolveUrl(src)}
                style={{
                  position: "absolute",
                  left: el.x * scale,
                  top: el.y * scale,
                  width: (el.width || 80) * scale,
                  height: el.height ? el.height * scale : "auto",
                  objectFit: "contain",
                }}
              />
            );
          }
          if (el.type === "text") {
            let text = "";
            if (el.field) {
              text = data[el.field] || "";
              if (el.optional && !text) return null;
            } else {
              text = interpolate(el.content, data);
            }
            return (
              <div
                key={i}
                style={{
                  position: "absolute",
                  left: el.align === "center" ? 0 : el.x * scale,
                  top: el.y * scale,
                  width: el.align === "center" ? w : (el.width || page.width) * scale,
                  fontSize: (el.fontSize || 12) * scale,
                  color: interpolate(el.color, data),
                  textAlign: el.align || "left",
                  fontWeight: el.font?.includes("Bold") ? "bold" : "normal",
                  fontStyle: el.font?.includes("Oblique") ? "italic" : "normal",
                  whiteSpace: "pre-line",
                  lineHeight: 1.2,
                }}
              >
                {text}
              </div>
            );
          }
          return null;
        })}
      </div>
    </div>
  );
}

export default CertificateTemplatePreview;
